import { useState, useRef } from "react";
// --- lottie ----------------------------------------------------------------
import Lottie from "lottie-react";
import contactLogo from "../assets/lotties/logo-purple.json";
// --- media ----------------------------------------------------------------
import previewAudio01 from "../assets/audio/preview-audio-01.mp3";
import previewAudio02 from "../assets/audio/preview-audio-02.mp3";
import previewAudio03 from "../assets/audio/preview-audio-03.mp3";
import previewAudio04 from "../assets/audio/preview-audio-04.mp3";
import previewAudio05 from "../assets/audio/preview-audio-05.mp3";
import previewAudio06 from "../assets/audio/preview-audio-06.mp3";
import previewAudio07 from "../assets/audio/preview-audio-07.mp3";
import previewAudio08 from "../assets/audio/preview-audio-08.mp3";
import previewAudio09 from "../assets/audio/preview-audio-09.mp3";
import previewAudio10 from "../assets/audio/preview-audio-10.mp3";
import {
  default02,
  kakaoIcon,
  default01,
  pauseIcon,
  playIcon,
  arrowIconMore,
  musicPortfolio01,
  musicPortfolio02,
  musicPortfolio03,
  musicWave,
} from "../assets/images";
// --- style ----------------------------------------------------------------
import "../styles/pages/Music.scss";

function Music() {
  // --- 상태 ----------------------------------------------------------------
  const [showAll, setShowAll] = useState(false); // 전체보기 | 숨기기
  const [playingId, setPlayingId] = useState(null); // 재생 중인 곡 (null: 재생 중 아님)
  const [currentTimes, setCurrentTimes] = useState({}); // 각 오디오의 현재 재생 시간
  const audioRefs = useRef({}); // 각 오디오 요소에 대한 참조

  // --- 데이터 ----------------------------------------------------------------
  // 1분 미리 듣기
  const previewItems = [
    {
      id: 1,
      genre: "RnB, BALLAD",
      title: "Fool",
      audioUrl: previewAudio01,
      image: default01,
    },
    {
      id: 2,
      genre: "RnB, MORDEN ROCK",
      title: "curse of nine tail",
      audioUrl: previewAudio02,
      image: default01,
    },
    {
      id: 3,
      genre: "RnB, BALLAD",
      title: "rain on me",
      audioUrl: previewAudio03,
      image: default01,
    },
    {
      id: 4,
      genre: "DANCE, K-POP",
      title: "click click",
      audioUrl: previewAudio04,
      image: default01,
    },
    {
      id: 5,
      genre: "RnB",
      title: "orbit",
      audioUrl: previewAudio05,
      image: default01,
    },
    {
      id: 6,
      genre: "SYNTH POP, RETRO",
      title: "flash back",
      audioUrl: previewAudio06,
      image: default01,
    },
    {
      id: 7,
      genre: "SYNTH POP, RETRO",
      title: "flash back2",
      audioUrl: previewAudio07,
      image: default01,
    },
    {
      id: 8,
      genre: "K-POP",
      title: "play my game",
      audioUrl: previewAudio08,
      image: default01,
    },
    {
      id: 9,
      genre: "HIPHOP",
      title: "famiglia code",
      audioUrl: previewAudio09,
      image: default01,
    },
    {
      id: 10,
      genre: "RnB, DANCE",
      title: "body language",
      audioUrl: previewAudio10,
      image: default01,
    },
  ];
  // 발매 음반
  const releseItems = [musicPortfolio01, musicPortfolio02, musicPortfolio03, default02]; // 이미지 전달 시 default02 이미지 교체

  // 1분 미리 듣기 전체보기 / 숨기기 제어
  const visibleItems = showAll ? previewItems : previewItems.slice(0, 6);

  // 시간 포맷팅 함수 (초를 mm:ss 형식으로 변환)
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // 오디오 재생/정지 핸들러
  const handlePlayPause = (itemId) => {
    const currentAudio = audioRefs.current[itemId];

    if (!currentAudio) return;

    // 같은 곡을 클릭한 경우: 재생 중이면 정지, 정지 중이면 재생
    if (playingId === itemId) {
      if (currentAudio.paused) {
        currentAudio.play();
      } else {
        currentAudio.pause();
        // 정지 시 currentTime은 유지 (다시 재생할 때 이어서 재생)
        setPlayingId(null);
      }
    } else {
      // 다른 곡을 클릭한 경우: 이전 곡 정지하고 새 곡 재생
      if (playingId !== null) {
        const previousAudio = audioRefs.current[playingId];
        if (previousAudio) {
          previousAudio.pause();
          previousAudio.currentTime = 0;
        }
      }

      currentAudio.play();
      setPlayingId(itemId);
    }
  };

  // 오디오 ref 콜백 - 각 오디오 요소가 마운트될 때 이벤트 리스너 추가
  const audioRefCallback = (itemId, audioElement) => {
    if (!audioElement) {
      // 언마운트 시 cleanup
      const audio = audioRefs.current[itemId];
      if (audio) {
        audio.removeEventListener("ended", audio._handleEnded);
        audio.removeEventListener("timeupdate", audio._handleTimeUpdate);
      }
      return;
    }

    // ref 저장
    audioRefs.current[itemId] = audioElement;

    // 이벤트 핸들러 생성
    const handleEnded = () => {
      // 곡이 끝까지 재생되면 재생 상태 및 시간 초기화
      setPlayingId(null);
      audioElement.currentTime = 0;
      setCurrentTimes((prev) => ({
        ...prev,
        [itemId]: 0,
      }));
    };

    const handleTimeUpdate = () => {
      setCurrentTimes((prev) => ({
        ...prev,
        [itemId]: audioElement.currentTime,
      }));
    };

    // 핸들러를 audio 요소에 저장 (cleanup 시 사용)
    audioElement._handleEnded = handleEnded;
    audioElement._handleTimeUpdate = handleTimeUpdate;

    // 이벤트 리스너 추가
    audioElement.addEventListener("ended", handleEnded);
    audioElement.addEventListener("timeupdate", handleTimeUpdate);
  };

  return (
    <section className="music page-wrapper">
      <h2 className="gfont max-12">
        Enjoy a 1-Minute preview
        <br />
        and purchase the track.
      </h2>

      {/* === PREVIEW (01.02 오디오 받으면 작업) ===  */}
      <section className="music__section preview-audio max-12">
        <div className="content-section__title">
          <span>MOB AI로 제작한 음악</span>
          <h3 className="gfont">1분 미리 들어보세요</h3>
        </div>

        <div className="preview-audio__content">
          <ul>
            {visibleItems.map((item) => (
              <li key={item.id} className={playingId === item.id ? "active" : ""}>
                <button type="button" className="cover-btn" onClick={() => handlePlayPause(item.id)}>
                  <div className="cover">
                    <audio ref={(el) => audioRefCallback(item.id, el)} src={item.audioUrl} preload="metadata"></audio>
                    {/* <img src={playingId === item.id ? musicWave : item.image} alt="" aria-hidden="true" className="cover-img" /> => 앨범커버가 존재할 경우 해당 코드로 변경*/}
                    <img src={musicWave} alt="" aria-hidden="true" className="cover-img" />

                    {/* --- 재생/정지 아이콘 --- */}
                    <div className="play-icon">
                      <img
                        src={playingId === item.id ? pauseIcon : playIcon}
                        alt={playingId === item.id ? "pause" : "play"}
                      />
                    </div>
                  </div>
                  <div className="description">
                    <span className="genre">{item.genre}</span>
                    <h4>{item.title}</h4>
                    <p className="duration">
                      {formatTime(currentTimes[item.id])}
                      <span>|</span>
                      <span>01:00</span>
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>

          {previewItems.length > 6 && (
            <button
              type="button"
              className={`btn-more ${showAll ? "active" : ""}`}
              onClick={() => setShowAll((prev) => !prev)}
            >
              {showAll ? "숨기기" : "전체보기"}
              <img src={arrowIconMore} alt="" aria-hidden="true" />
            </button>
          )}
        </div>
      </section>

      {/* === STUDIO BANNER === */}
      <section className="mob-live-studio">
        <div className="content-section__title max-12">
          <span>당신의 목소리가 음악이 되는 순간</span>
          <h3 className="gfont">RECORDING STUDIO</h3>
        </div>
        <div className="studio-content music">
          <small>Service Inquiry</small>
          <h4>
            MOB STUDIO에서
            <br />
            원하는 곡 주문하기
          </h4>
          <div className="kakao-link">
            <a href="https://pf.kakao.com/_QLTxgn" target="_blank" rel="noopener noreferrer">
              <img src={kakaoIcon} alt="" aria-hidden="true" />
              카카오 채널 문의
            </a>
          </div>
        </div>
      </section>

      {/* === TICKER === */}
      <section className="music__section ticker gfont">
        <div className="ticker__title max-12">
          <p>지금도 많은 분들이</p>
          <h3>음반 제작 발매 중</h3>
        </div>

        <div className="ticker__content">
          <div className="ticker__viewport">
            <div className="ticker__track" aria-hidden="true">
              {releseItems.map((src, i) => (
                <div className="ticker__item" key={`a-${i}`}>
                  <img src={src} alt="" aria-hidden="true" />
                </div>
              ))}
            </div>

            <div className="ticker__track" aria-hidden="true">
              {releseItems.map((src, i) => (
                <div className="ticker__item" key={`b-${i}`}>
                  <img src={src} alt="" aria-hidden="true" />
                </div>
              ))}
            </div>
            <div className="ticker__track" aria-hidden="true">
              {releseItems.map((src, i) => (
                <div className="ticker__item" key={`b-${i}`}>
                  <img src={src} alt="" aria-hidden="true" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === INQUIRY === */}
      <div className="content-section__contact">
        <p className="desc">ALL MAKERS, ONE STAGE. 지금 당신의 무대를 시작하세요</p>
        <h3 className="gfont title">CONTACT US</h3>
        <Lottie animationData={contactLogo} loop autoplay className="contact-lottie" />

        <div className="kakao-link">
          <span>지금 시작해 보세요.</span>
          <a href="https://pf.kakao.com/_QLTxgn" target="_blank" rel="noopener noreferrer">
            <img src={kakaoIcon} alt="" aria-hidden="true" />
            카카오 채널 문의
          </a>
        </div>
      </div>
    </section>
  );
}

export default Music;
